"use client";
import { motion, useReducedMotion } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { caseStudyData } from "@/data/CaseStudyData";
import { caseStudySchedule } from "@/data/CaseStudyPageData";
import { fadeInUp, gridCellRise } from "@/lib/animation/variant";
import { cn } from "@/lib/utils/utils";

const CELL_CLASS = "border-r border-b border-black/10 p-4 align-top text-left";
const LABEL_CLASS =
  "bg-black/[0.035] font-mono text-[10px] leading-loose tracking-[0.15em] uppercase";

const CaseStudySchedule = () => {
  const reduceMotion = useReducedMotion();

  return (
    <section className="w-full bg-background pt-16 md:pt-24">
      <Container className="flex flex-col gap-6 px-4 lg:px-10">
        <div className="space-y-2">
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionSubHeading subtitle="Stage by stage — strategy, discovery, concept, refinement, system" />
          </motion.div>

          <motion.div
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <SectionTitle title="Tonight's schedule" className="max-w-4xl" />
          </motion.div>
        </div>

        {/* A real table: this is tabular data, and the row/column headers are
            what make it readable to a screen reader and worth indexing. */}
        <div className="mt-2 overflow-x-auto border border-black/10">
          <motion.table
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.12 }}
            className="w-full min-w-225 table-fixed border-collapse"
          >
            <caption className="sr-only">
              What each stage decided, engagement by engagement
            </caption>
            <thead>
              <tr className="[&>*:last-child]:border-r-0">
                <th
                  scope="col"
                  className={cn(
                    CELL_CLASS,
                    LABEL_CLASS,
                    "w-33 text-muted-foreground",
                  )}
                >
                  Stage
                </th>
                {caseStudyData.map((study, column) => (
                  <motion.th
                    key={study.id}
                    scope="col"
                    variants={gridCellRise}
                    custom={(column + 1) * 3}
                    className={cn(
                      CELL_CLASS,
                      LABEL_CLASS,
                      "font-semibold text-accent-foreground",
                    )}
                  >
                    {study.name ?? study.title}
                  </motion.th>
                ))}
              </tr>
            </thead>

            <tbody>
              {caseStudySchedule.map((row, rowIndex) => (
                <tr
                  key={row.stage}
                  className="[&>*:last-child]:border-r-0 last:[&>*]:border-b-0"
                >
                  <th
                    scope="row"
                    className={cn(
                      CELL_CLASS,
                      LABEL_CLASS,
                      "text-accent-foreground",
                    )}
                  >
                    <span className="block text-muted-foreground">
                      {row.weeks}
                    </span>
                    <span className="block font-semibold">{row.stage}</span>
                  </th>

                  {caseStudyData.map((study, column) => {
                    const cell = row.cells.find(
                      (item) => item.slug === study.slug,
                    );
                    if (!cell) return null;

                    return (
                      <motion.td
                        key={`${row.stage}-${study.id}`}
                        variants={gridCellRise}
                        // row + column, so the grid fills on a diagonal rather
                        // than sweeping one row at a time.
                        custom={(rowIndex + column + 2) * 3}
                        className={cn(
                          CELL_CLASS,
                          "relative text-xs leading-relaxed",
                          cell.decisive
                            ? "bg-primary text-accent-foreground/80"
                            : "text-muted-foreground",
                        )}
                      >
                        {cell.decisive && (
                          <motion.span
                            aria-hidden
                            animate={
                              reduceMotion ? undefined : { opacity: [0.5, 0] }
                            }
                            transition={{ duration: 3.2, repeat: Infinity }}
                            className="pointer-events-none absolute inset-0 border-2 border-accent-foreground opacity-0"
                          />
                        )}
                        <b
                          className={cn(
                            "mb-1.5 block text-lg leading-tight font-bold uppercase",
                            cell.decisive
                              ? "text-accent-foreground"
                              : "text-foreground",
                          )}
                        >
                          {cell.title}
                        </b>
                        {cell.detail}
                      </motion.td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </motion.table>
        </div>

        <p className="flex items-center gap-2.5 font-mono text-[10px] leading-loose tracking-[0.15em] text-muted-foreground uppercase">
          <span aria-hidden className="size-2.5 shrink-0 bg-primary" />
          Highlighted cell — the stage that decided the outcome for that
          engagement
        </p>
      </Container>
    </section>
  );
};

export default CaseStudySchedule;
